import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Signal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractDynamicComponent } from '@components/abstracts';
import { FormulaireContactB2C } from '@components/formulaire-contact-b2c/models';
import { FormValid } from '@components/formulaire-rdv/models';
import { StoreLocatorService } from '@components/store-locator/store-locator.service';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button';
import { ComboboxComponent } from '@shared/components/combobox';
import { ImageComponent } from '@shared/components/image';
import { SelectComponent } from '@shared/components/select';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { HandleHtmlLinksDirective, ViewportObserverDirective } from '@shared/directives';
import { LabelKey, StoreWithUuid } from '@shared/models';
import { ContactB2cService } from '@shared/services/contact-b2c';
import { Contact } from '@shared/services/contact-b2c/models';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { CurrentLanguageService, REGIONS } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import {
  envConfiguredNetworkId,
  formatPhone,
  isEmailValid,
  normalizeAndHash,
  validatePhoneByCountry,
} from '@shared/utils';
import { CommonStore } from '@stores';
import { combineLatest, EMPTY, map, of, switchMap, take } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Component({
  selector: 'app-formulaire-contact-b2c',
  imports: [
    TitleComponent,
    HandleHtmlLinksDirective,
    SvgComponent,
    ButtonComponent,
    TranslatePipe,
    SelectComponent,
    ImageComponent,
    ViewportObserverDirective,
    ComboboxComponent,
  ],
  templateUrl: './formulaire-contact-b2c.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormulaireContactB2cComponent extends AbstractDynamicComponent<FormulaireContactB2C> {
  readonly formStart = viewChild<ElementRef<HTMLDivElement>>('formStart');
  protected readonly callMePhone = computed(() => formatPhone(this.data.phoneSection.phoneNumber) ?? '');
  protected readonly requestType = signal<string>(null!);
  protected readonly requestOrderType = signal<string>(null!);
  protected readonly requestInformationType = signal<string>(null!);
  protected readonly gender = signal<'MALE' | 'FEMALE'>(null!);
  protected readonly firstName = signal<string>('');
  protected readonly lastName = signal<string>('');
  protected readonly zipcode = signal<string>('');
  protected readonly city = signal<string>('');
  protected readonly phone = signal<string>('');
  protected readonly email = signal<string>('');
  protected readonly isFormSubmitted = signal(false);
  protected readonly isSending = signal(false);
  protected readonly message = signal<string>('');
  protected readonly subscribeEmail = signal(false);
  protected readonly subscribeSms = signal(false);
  protected readonly isRequestInformationType = signal(false);
  protected readonly isRequestOrderType = signal(false);
  protected readonly stores = signal<LabelKey[]>([]);
  protected readonly isInputVisible = signal(false);
  protected readonly hasSubmittedSuccessfully = signal(false);
  protected readonly cityOptions = signal<LabelKey[]>([]);
  protected readonly zipcodeNotFound = signal(false);
  protected readonly isFormValid: Signal<FormValid> = computed(() => {
    let result: FormValid = {
      status: 'OK',
    };
    const errors: FormValid['errors'] = {};

    if (this.requestType() === null) {
      errors.requestType = 'GLOBAL.FORMS.REQUEST_TYPE_REQUIRED_ERROR';
    }

    if (this.isRequestInformationType() && this.requestInformationType() === null) {
      errors.requestInformationType = 'GLOBAL.FORMS.REQUEST_INFORMATION_TYPE_REQUIRED_ERROR';
    }

    if (this.isRequestOrderType() && this.requestOrderType() === null) {
      errors.requestOrderType = 'GLOBAL.FORMS.REQUEST_ORDER_TYPE_REQUIRED_ERROR';
    }

    if (this.gender() === null) {
      errors.gender = 'GLOBAL.FORMS.GENDER_REQUIRED_ERROR';
    }

    if (this.firstName().length < 3) {
      errors.firstName = 'GLOBAL.FORMS.FIRST_NAME_MIN_LENGTH_ERROR';
    }

    if (this.lastName().length < 2) {
      errors.lastName = 'GLOBAL.FORMS.LAST_NAME_MIN_LENGTH_ERROR';
    }

    if (this.zipcode().length === 0) {
      errors.zipcode = 'GLOBAL.FORMS.ZIPCODE_REQUIRED_ERROR';
    } else if (!this.postalCodePattern.test(this.zipcode())) {
      errors.zipcode = 'GLOBAL.FORMS.ZIPCODE_FORMAT_ERROR';
    } else if (this.zipcodeNotFound()) {
      errors.zipcode = 'GLOBAL.FORMS.ZIPCODE_NOT_FOUND_ERROR';
    }

    if (this.email().length === 0) {
      errors.email = 'GLOBAL.FORMS.EMAIL_REQUIRED_ERROR';
    } else if (!isEmailValid(this.email())) {
      errors.email = 'GLOBAL.FORMS.EMAIL_INVALID_ERROR';
    }

    if (this.message().length === 0) {
      errors.message = 'GLOBAL.FORMS.MESSAGE_REQUIRED_ERROR';
    }

    if (this.phone().length === 0) {
      errors.phone = 'GLOBAL.FORMS.PHONE_REQUIRED_ERROR';
    } else if (!validatePhoneByCountry(this.phone(), this.language.region)) {
      errors.phone = 'GLOBAL.FORMS.PHONE_FORMAT_ERROR';
    }

    if (this.city().length === 0) {
      errors.city = 'GLOBAL.FORMS.CITY_REQUIRED_ERROR';
    }

    if (Object.keys(errors).length > 0) {
      result = {
        status: 'KO',
        errors: errors,
      };
    }

    return result;
  });
  private readonly contactB2cService = inject(ContactB2cService);
  private readonly emailAndPhoneSha256: Signal<{ email: string; phone: string }> = toSignal(
    combineLatest([
      toObservable(this.email), // reacts to email changes
      toObservable(this.phone), // reacts to email changes
      toObservable(this.isFormValid), // reacts to form validity changes
    ]).pipe(
      switchMap(([email, phone, form]) => {
        if (form.status !== 'OK' || !email || !phone) {
          return of({ email: '', phone: '' });
        }
        return combineLatest([normalizeAndHash(email), normalizeAndHash(phone)]).pipe(
          map(([emailHash, phoneHash]) => ({
            email: emailHash,
            phone: phoneHash,
          })),
          catchError((err: unknown) => {
            this.logs.warn(
              'FormulaireContactB2cComponent',
              'Failed to hash email/phone for GTM enhanced conversions',
              err
            );
            return of({ email: '', phone: '' });
          })
        );
      })
    ),
    { initialValue: { email: '', phone: '' } }
  );
  private readonly logs = inject(LogsService);
  private readonly language = inject(CurrentLanguageService).language;
  private readonly postalCodePattern = new RegExp(REGIONS[this.language.region].postalCodePattern);
  private readonly storeLocatorService = inject(StoreLocatorService);
  private readonly elasticSearchService = inject(ElasticSearchService);
  private readonly commonStore = inject(CommonStore);

  constructor() {
    super();
    effect(() => {
      const requestType = this.requestType();
      const REQUEST_TYPE_INFORMATION_KEY = untracked(
        this.commonStore.selectPath((state) => state.forms.requestTypeInformationKey)
      );
      const REQUEST_TYPE_ORDER_KEY = untracked(this.commonStore.selectPath((state) => state.forms.requestTypeOrderKey));
      this.logs.trace('FormulaireContactB2cComponent', 'requestType', {
        REQUEST_TYPE_INFORMATION_KEY,
        REQUEST_TYPE_ORDER_KEY,
      });
      if (requestType === null) return;
      switch (requestType) {
        case REQUEST_TYPE_INFORMATION_KEY:
          this.isRequestInformationType.set(true);
          break;
        case REQUEST_TYPE_ORDER_KEY:
          this.isRequestOrderType.set(true);
          break;
        default:
          this.isRequestInformationType.set(false);
          this.isRequestOrderType.set(false);
          this.requestInformationType.set(null!);
          this.requestOrderType.set(null!);
      }
    });
    effect(() => {
      const zipcode = this.zipcode();
      const cityOptions = untracked(this.cityOptions);
      if (this.postalCodePattern.test(zipcode)) {
        this.onZipcodeChange(zipcode);
      } else if (cityOptions.length > 0) {
        this.cityOptions.set([]);
      }
    });
    effect(() => {
      if (this.isRequestOrderType() && untracked(() => this.stores()).length === 0) {
        this.elasticSearchService
          .getAllStores()
          .pipe(
            take(1),
            tap((stores) => {
              const storesNameId = stores.map((store) => {
                const { name, uuid } = store as StoreWithUuid;
                return { label: name ?? '', key: uuid ?? '' };
              });
              this.stores.set(storesNameId);
            })
          )
          .subscribe({ error: (err) => this.logs.error('FormulaireContactB2cComponent', 'getAllStores failed', err) });
      }
    });
  }

  onRequestOrderTypeSelected($event: LabelKey): void {
    const storeUuid = $event.key;
    if (storeUuid) this.requestOrderType.set(storeUuid);
  }

  onRequestTypeInformationSelected($event: string): void {
    const requestTypeKey = $event;
    const requestTypeLabel = this.data.formSection.requestTypesInformations.find(
      (type) => type.key === requestTypeKey
    )?.label;
    if (requestTypeLabel) {
      this.requestInformationType.set(requestTypeLabel);
    }
  }

  onCitySelected($event: string): void {
    this.city.set(this.cityOptions().find((o) => o.key === $event)?.label ?? '');
  }

  onInputChange($event: Event): void {
    const target = $event.target as HTMLInputElement;
    const { id, value, checked, type } = target;
    this.logs.trace(
      'FormulaireContactB2cComponent',
      `onInputChange - ${id} : ${type === 'checkbox' ? checked : value}`
    );
    switch (id) {
      case 'genderMale':
      case 'genderFemale':
        this.gender.set(value as 'MALE' | 'FEMALE');
        break;
      case 'firstName':
        this.firstName.set(value);
        break;
      case 'lastName':
        this.lastName.set(value);
        break;
      case 'email':
        this.email.set(value);
        break;
      case 'phone':
        this.phone.set(value);
        break;
      case 'zipcode':
        this.zipcode.set(value);
        break;
      case 'message':
        this.message.set(value);
        break;
      case 'subscribeEmail':
        this.subscribeEmail.set(checked);
        break;
      case 'subscribeSms':
        this.subscribeSms.set(checked);
        break;
    }
  }

  onSubmit(buttonLabel: string): void {
    if (this.isSending() || this.hasSubmittedSuccessfully()) return;
    this.logs.debug('FormulaireContactB2cComponent', 'onSubmit', this.isFormValid());
    this.isFormSubmitted.set(true);
    if (this.isFormValid().status === 'KO') {
      this.scrollToFormStart();
    } else {
      this.isSending.set(true);
      const requestOrderType = this.requestOrderType();
      const toSaved: Contact = {
        type: this.requestType(),
        ssType: this.requestInformationType() ?? '',
        ...(requestOrderType && { store: `/stores/${requestOrderType}` }),
        civilite: this.gender() === 'MALE' ? 1 : 2,
        firstname: this.firstName(),
        lastname: this.lastName(),
        zipcode: this.zipcode(),
        city: this.city(),
        phone: this.phone().replace(/\s/g, ''),
        email: this.email(),
        message: this.message(),
        optin: this.subscribeEmail(),
        optinSms: this.subscribeSms(),
        network: envConfiguredNetworkId(this.language.region),
      };
      this.logs.trace('FormulaireContactB2cComponent', 'toSaved', {
        type: toSaved.type,
        ssType: toSaved.ssType,
        store: toSaved.store,
        network: toSaved.network,
      });
      this.contactB2cService
        .create(toSaved, true)
        .pipe(
          take(1),
          tap(() => {
            this.logs.info('FormulaireContactB2cComponent', 'Contact form submission completed successfully');
            const gtm = this.data.gtmConfigSubmit;
            this.emitGtmEvent({
              event: gtm?.event,
              group: gtm?.group,
              description: gtm?.description,
              context: gtm?.context,
              button_text_value: buttonLabel,
              event_type: 'submit',
              user_data: {
                user_email: this.emailAndPhoneSha256().email,
                user_phone: this.emailAndPhoneSha256().phone,
              },
            });
            this.hasSubmittedSuccessfully.set(true);
            this.scrollToFormStart();
          }),
          catchError((e) => {
            this.logs.error('FormulaireContactB2cComponent', 'Failed to submit contact form', e);
            this.isSending.set(false);
            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  private onZipcodeChange(zipcode: string): void {
    this.city.set('');
    this.zipcodeNotFound.set(false);
    this.updateCityOptions(zipcode);
  }

  private updateCityOptions(zipcode: string): void {
    if (zipcode && zipcode.length !== 5) return;
    this.storeLocatorService
      .getLocation(zipcode, false)
      .pipe(
        take(1),
        tap((locations) => {
          if (locations && locations.length > 0) {
            this.logs.trace('FormulaireContactB2cComponent', `Received ${locations.length} locations`, locations);
            const options = locations.reduce<LabelKey[]>((acc, location) => {
              if (/^\d+$/.test(location.name)) return acc;
              if (acc.find((el) => el.label === location.name)) return acc;
              acc.push({ label: location.name, key: location.id });
              return acc;
            }, []);
            if (options.length > 0) {
              this.cityOptions.set(options);
            } else {
              this.cityOptions.set([]);
              this.zipcodeNotFound.set(true);
              this.logs.warn('FormulaireContactB2cComponent', 'No valid city found for zipcode');
            }
          } else {
            this.cityOptions.set([]);
            this.zipcodeNotFound.set(true);
            this.logs.warn('FormulaireContactB2cComponent', 'No locations found');
          }
        })
      )
      .subscribe();
  }

  private scrollToFormStart(): void {
    const elRef = this.formStart();
    if (!elRef || this.isInputVisible()) return;
    elRef.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
}
